/**
 * ActivityTracker — mounted once at the app root for authenticated users.
 *
 *  1. Presence + active-time: while the tab is visible and the user has
 *     actually interacted (mouse / keyboard / touch) since the last beat,
 *     POST /activity/heartbeat every 60s. The server accumulates active
 *     seconds and refreshes last-seen (drives the online dot + reports).
 *  2. Idle auto-logout: for staff (managers/admin), if there is NO interaction
 *     for `autoLogoutMinutes`, log out and bounce to /login.
 *
 * Thresholds come from /activity/config (admin-configurable in Security).
 */
import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, unwrap } from "@/lib/api";
import { useMe, useLogout } from "@/hooks/useAuth";

const HEARTBEAT_MS = 60_000;
const IDLE_CHECK_MS = 20_000;
const INTERACTION_EVENTS = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"] as const;

interface ActivityConfig { offlineAfterMinutes: number; autoLogoutMinutes: number; autoLogoutEnabled: boolean; }

export default function ActivityTracker() {
  const { data: me } = useMe();
  const logout = useLogout();
  const navigate = useNavigate();
  const authed = !!me;

  const cfg = useQuery({
    queryKey: ["activity-config"],
    queryFn: async () => unwrap<ActivityConfig>((await api.get("/activity/config")).data),
    enabled: authed,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const lastInteraction = useRef<number>(Date.now());
  const interactedSinceBeat = useRef<boolean>(false);
  const loggingOut = useRef<boolean>(false);

  // Record real user interaction (throttled to once/second via the ref check).
  useEffect(() => {
    if (!authed) return;
    const onInteract = () => {
      lastInteraction.current = Date.now();
      interactedSinceBeat.current = true;
    };
    for (const ev of INTERACTION_EVENTS) window.addEventListener(ev, onInteract, { passive: true });
    return () => { for (const ev of INTERACTION_EVENTS) window.removeEventListener(ev, onInteract); };
  }, [authed]);

  // Heartbeat: while the tab is visible, ping every 60s so the user shows
  // "online" (presence) even if just reading. The `active` flag tells the
  // server whether to also accrue active-TIME (real interaction only).
  useEffect(() => {
    if (!authed) return;
    const beat = () => {
      if (document.visibilityState !== "visible") return;
      const active = interactedSinceBeat.current;
      interactedSinceBeat.current = false;
      api.post("/activity/heartbeat", { active }).catch(() => {});
    };
    beat(); // fire immediately so the online dot goes green right after login
    const id = window.setInterval(beat, HEARTBEAT_MS);
    return () => window.clearInterval(id);
  }, [authed]);

  // Idle auto-logout (staff only).
  useEffect(() => {
    if (!authed || !cfg.data?.autoLogoutEnabled) return;
    const limitMs = Math.max(5, cfg.data.autoLogoutMinutes) * 60_000;
    const id = window.setInterval(async () => {
      if (loggingOut.current) return;
      if (Date.now() - lastInteraction.current < limitMs) return;
      loggingOut.current = true;
      try { await logout.mutateAsync(); } catch { /* ignore */ }
      navigate("/login", { replace: true });
    }, IDLE_CHECK_MS);
    return () => window.clearInterval(id);
  }, [authed, cfg.data?.autoLogoutEnabled, cfg.data?.autoLogoutMinutes, logout, navigate]);

  return null;
}
